import React, { useState, useEffect } from 'react';
import { 
  Card, CardContent, Typography, TextField, Button, Table, TableBody, 
  TableCell, TableContainer, TableHead, TableRow, Paper, 
  Grid, Select, MenuItem, FormLabel, Checkbox, FormControlLabel, Box,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { ArrowUpward, ArrowDownward, Add, Close, Settings } from '@mui/icons-material';

import { ActionButtons } from './shared/ActionButtons';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/apiHelpers';
import apiClient from '../utils/axiosConfig';
import { useParams } from 'react-router';
import { useQueryParams } from '../hooks/useQueryParams';
import ServerManagement from './ServerManagement';

function MonitoredAccounts() {
  const { id } = useParams();
  const { queryParams, setQueryParam } = useQueryParams();
  
  // Get user from token verification
  const [user, setUser] = useState(null);
  const canEdit = user && ['admin', 'account_onboarding'].includes(user.role);
  
  // Verify token and get user info
  useEffect(() => {
    const checkUser = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await apiGet('/api/verify-token');
          setUser(response.data.user);
        } catch (error) {
          console.warn('Token verification failed:', error);
        }
      }
    };
    checkUser();
  }, []);
  const [records, setRecords] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [servers, setServers] = useState([]);
  const [showServerManagement, setShowServerManagement] = useState(false);
  const [filterManufacturer, setFilterManufacturer] = useState(queryParams.manufacturer || '');
  const [filterAccount, setFilterAccount] = useState(queryParams.account || '');
  const [filterFilePrefix, setFilterFilePrefix] = useState(queryParams.prefix || '');
  const [filterServerId, setFilterServerId] = useState(queryParams.serverId || '');
  const [filterDataInterval, setFilterDataInterval] = useState(queryParams.dataInterval || '');
  const [filterMsh5, setFilterMsh5] = useState(queryParams.msh5 || '');
  const [filterMonitored, setFilterMonitored] = useState(queryParams.monitored || '');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [form, setForm] = useState({
    manufacturer_name: '',
    account_name: '',
    file_path_prefix: '',
    server_id: '',
    data_interval: '',
    msh5: '',
    is_monitored: true
  });
  const [editForm, setEditForm] = useState({
    manufacturer_name: '',
    account_name: '',
    file_path_prefix: '',
    server_id: '',
    data_interval: '',
    msh5: '',
    is_monitored: true
  });
  const [showAccountSuggestions, setShowAccountSuggestions] = useState(false);
  const [showEditAccountSuggestions, setShowEditAccountSuggestions] = useState(false);
  const [sortConfig, setSortConfig] = useState({ field: null, order: null }); // field: 'manufacturer'|'account'|'monitored', order: 'asc'|'desc'|null
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  // Handle direct item access via URL parameter
  useEffect(() => {
    if (id && records.length > 0 && canEdit) {
      const record = records.find(r => `${r.manufacturer_id}-${r.account_id}` === id);
      if (record) {
        startEdit(record);
      }
    }
  }, [id, records, canEdit]);

  const fetchData = async () => {
    try {
      const [recordsRes, manufacturersRes, accountsRes, serversRes] = await Promise.all([
        apiGet('/api/monitored-accounts'),
        apiGet('/api/manufacturers'),
        apiGet('/api/accounts?limit=1000'),
        apiGet('/api/servers')
      ]);

      setRecords(recordsRes.data || []);
      setManufacturers(manufacturersRes.data || []);
      setAccounts(Array.isArray(accountsRes.data.data) ? accountsRes.data.data : []);
      setServers(serversRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const addRecord = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    try {
      await apiPost('/api/monitored-accounts', form);
      setForm({ manufacturer_name: '', account_name: '', file_path_prefix: '', server_id: '', data_interval: '', msh5: '', is_monitored: true });
      setAddDialogOpen(false);
      fetchData();
    } catch (error) {
      setErrorMessage(error.response?.data?.error || 'Failed to add monitored account');
    }
  };

  const updateRecord = async (manufacturerId, accountId, data) => {
    await apiPut(`/api/monitored-accounts/${manufacturerId}/${accountId}`, data);
    fetchData();
  };

  const deleteRecord = async (manufacturerId, accountId) => {
    if (window.confirm('Are you sure?')) {
      await apiDelete(`/api/monitored-accounts/${manufacturerId}/${accountId}`);
      fetchData();
    }
  };

  const openEditDialog = (record) => {
    setSelectedRecord(record);
    setEditForm({
      manufacturer_name: record.manufacturer_name,
      account_name: record.account_name,
      file_path_prefix: record.file_path_prefix,
      server_id: record.server_id || '',
      data_interval: record.data_interval || '',
      msh5: record.msh5 || '',
      is_monitored: record.is_monitored
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    try {
      // If manufacturer or account changed, we need to delete old and create new
      const manufacturerChanged = editForm.manufacturer_name !== selectedRecord.manufacturer_name;
      const accountChanged = editForm.account_name !== selectedRecord.account_name;
      
      if (manufacturerChanged || accountChanged) {
        // Delete old record and create new one
        await apiDelete(`/api/monitored-accounts/${selectedRecord.manufacturer_id}/${selectedRecord.account_id}`);
        await apiPost('/api/monitored-accounts', editForm);
      } else {
        // Just update existing record
        await apiPut(`/api/monitored-accounts/${selectedRecord.manufacturer_id}/${selectedRecord.account_id}`, {
          file_path_prefix: editForm.file_path_prefix,
          server_id: editForm.server_id,
          data_interval: editForm.data_interval,
          msh5: editForm.msh5,
          is_monitored: editForm.is_monitored
        });
      }
      
      setEditDialogOpen(false);
      setSelectedRecord(null);
      fetchData();
    } catch (error) {
      setErrorMessage(error.response?.data?.error || 'Failed to update monitored account');
    }
  };

  const handleEditDelete = async () => {
    if (window.confirm('Are you sure you want to delete this monitored account?')) {
      try {
        await apiDelete(`/api/monitored-accounts/${selectedRecord.manufacturer_id}/${selectedRecord.account_id}`);
        setEditDialogOpen(false);
        setSelectedRecord(null);
        fetchData();
      } catch (error) {
        setErrorMessage(error.response?.data?.error || 'Failed to delete monitored account');
      }
    }
  };

  const handleSort = (field) => {
    if (sortConfig.field === field) {
      // Same field clicked - cycle through: asc -> desc -> null
      if (sortConfig.order === 'asc') {
        setSortConfig({ field, order: 'desc' });
      } else if (sortConfig.order === 'desc') {
        setSortConfig({ field: null, order: null });
      }
    } else {
      // New field clicked - start with asc
      setSortConfig({ field, order: 'asc' });
    }
  };

  const getServerName = (record) => {
    // Use server_name from database if available, otherwise fallback to server lookup
    if (record.server_name) {
      return record.server_name;
    }
    if (!record.server_id) return '';
    const server = servers.find(s => s.server_id === record.server_id);
    return server ? server.server_name : record.server_id;
  };

  const getSortedRecords = (filteredRecords) => {
    if (!sortConfig.field || !sortConfig.order) return filteredRecords;
    
    return [...filteredRecords].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortConfig.field) {
        case 'manufacturer':
          aValue = a.manufacturer_name.toLowerCase();
          bValue = b.manufacturer_name.toLowerCase();
          break;
        case 'account':
          aValue = a.account_name.toLowerCase();
          bValue = b.account_name.toLowerCase();
          break;
        case 'monitored':
          aValue = a.is_monitored;
          bValue = b.is_monitored;
          if (sortConfig.order === 'asc') {
            return aValue === bValue ? 0 : aValue ? -1 : 1;
          } else {
            return aValue === bValue ? 0 : aValue ? 1 : -1;
          }
        default:
          return 0;
      }
      
      if (sortConfig.field === 'manufacturer' || sortConfig.field === 'account') {
        if (sortConfig.order === 'asc') {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      }
      
      return 0;
    });
  };

  const getSortIcon = (field) => {
    if (sortConfig.field !== field) {
      return <ArrowUpward sx={{ fontSize: 18, color: 'grey.400' }} />;
    }
    if (sortConfig.order === 'asc') {
      return <ArrowUpward sx={{ fontSize: 18, color: 'primary.main' }} />;
    } else if (sortConfig.order === 'desc') {
      return <ArrowDownward sx={{ fontSize: 18, color: 'primary.main' }} />;
    }
    return <ArrowUpward sx={{ fontSize: 18, color: 'grey.400' }} />;
  };

  return (
    <Box sx={{ p: 1 ,m:0 }}>
      {showServerManagement ? (
        <ServerManagement onClose={() => setShowServerManagement(false)} />
      ) : (
        <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0 }}>
        <Typography variant="h6">Monitored Accounts</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500 }}>
            Monitored: {records.filter(r => r.is_monitored).length}
          </Typography>
          <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 500 }}>
            Unmonitored: {records.filter(r => !r.is_monitored).length}
          </Typography>
          {canEdit && (
            <>
              <Button
                variant="outlined"
                startIcon={<Settings />}
                onClick={() => setShowServerManagement(true)}
                sx={{ textTransform: 'none' }}
              >
                Manage Servers
              </Button>
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={() => setAddDialogOpen(true)}
              >
                Add New
              </Button>
            </>
          )}
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ boxShadow: 2, maxHeight: 600, overflow: 'auto', mt: 1 }}>
        <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, width: '18%', cursor: 'pointer', userSelect: 'none', bgcolor: 'grey.50', position: 'sticky', top: 0, zIndex: 3, py: 1 }} onClick={() => handleSort('manufacturer')}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          Manufacturer
                          {getSortIcon('manufacturer')}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, width: '18%', cursor: 'pointer', userSelect: 'none', bgcolor: 'grey.50', position: 'sticky', top: 0, zIndex: 3, py: 1 }} onClick={() => handleSort('account')}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          Account
                          {getSortIcon('account')}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, width: '25%', bgcolor: 'grey.50', position: 'sticky', top: 0, zIndex: 3, py: 1 }}>File Prefix</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: '15%', bgcolor: 'grey.50', position: 'sticky', top: 0, zIndex: 3, py: 1 }}>Server Name</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: '12%', bgcolor: 'grey.50', position: 'sticky', top: 0, zIndex: 3, py: 1 }}>Data Interval</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: '12%', bgcolor: 'grey.50', position: 'sticky', top: 0, zIndex: 3, py: 1 }}>MSH-5</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: '15%', cursor: 'pointer', userSelect: 'none', bgcolor: 'grey.50', position: 'sticky', top: 0, zIndex: 3, py: 1 }} onClick={() => handleSort('monitored')}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          Monitored
                          {getSortIcon('monitored')}
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ py: 0.5, bgcolor: 'grey.50', position: 'sticky', top: 41, zIndex: 4 }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Manufacturer..."
                          value={filterManufacturer}
                          onChange={(e) => {
                            setFilterManufacturer(e.target.value);
                            setQueryParam('manufacturer', e.target.value);
                          }}
                          sx={{ bgcolor: 'white' }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.5, bgcolor: 'grey.50', position: 'sticky', top: 41, zIndex: 4 }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Account..."
                          value={filterAccount}
                          onChange={(e) => {
                            setFilterAccount(e.target.value);
                            setQueryParam('account', e.target.value);
                          }}
                          sx={{ bgcolor: 'white' }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.5, bgcolor: 'grey.50', position: 'sticky', top: 41, zIndex: 4 }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="File Prefix..."
                          value={filterFilePrefix}
                          onChange={(e) => {
                            setFilterFilePrefix(e.target.value);
                            setQueryParam('prefix', e.target.value);
                          }}
                          sx={{ bgcolor: 'white' }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.5, bgcolor: 'grey.50', position: 'sticky', top: 41, zIndex: 4 }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Server Name..."
                          value={filterServerId}
                          onChange={(e) => {
                            setFilterServerId(e.target.value);
                            setQueryParam('serverId', e.target.value);
                          }}
                          sx={{ bgcolor: 'white' }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.5, bgcolor: 'grey.50', position: 'sticky', top: 41, zIndex: 4 }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Data Interval..."
                          value={filterDataInterval}
                          onChange={(e) => {
                            setFilterDataInterval(e.target.value);
                            setQueryParam('dataInterval', e.target.value);
                          }}
                          sx={{ bgcolor: 'white' }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.5, bgcolor: 'grey.50', position: 'sticky', top: 41, zIndex: 4 }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="MSH-5..."
                          value={filterMsh5}
                          onChange={(e) => {
                            setFilterMsh5(e.target.value);
                            setQueryParam('msh5', e.target.value);
                          }}
                          sx={{ bgcolor: 'white' }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.5, bgcolor: 'grey.50', position: 'sticky', top: 41, zIndex: 4 }}>
                        <Select
                          fullWidth
                          size="small"
                          value={filterMonitored}
                          onChange={(e) => {
                            setFilterMonitored(e.target.value);
                            setQueryParam('monitored', e.target.value);
                          }}
                          displayEmpty
                          sx={{ bgcolor: 'white' }}
                        >
                          <MenuItem value="">All</MenuItem>
                          <MenuItem value="true">True</MenuItem>
                          <MenuItem value="false">False</MenuItem>
                        </Select>
                      </TableCell>
                      {canEdit && <TableCell sx={{ py: 0.5, bgcolor: 'grey.50', position: 'sticky', top: 41, zIndex: 4 }} />}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getSortedRecords(
                      records.filter(record => {
                        const matchesManufacturer = !filterManufacturer || record.manufacturer_name.toLowerCase().includes(filterManufacturer.toLowerCase());
                        const matchesAccount = !filterAccount || record.account_name.toLowerCase().includes(filterAccount.toLowerCase());
                        const lastSegment = (record.file_path_prefix || '').split('/').filter(segment => segment).pop() || '';
                        const matchesFilePrefix = !filterFilePrefix || lastSegment.toLowerCase().includes(filterFilePrefix.toLowerCase());
                        const matchesServerId = !filterServerId || getServerName(record).toLowerCase().includes(filterServerId.toLowerCase());
                        const matchesDataInterval = !filterDataInterval || String(record.data_interval || '').includes(filterDataInterval);
                        const matchesMsh5 = !filterMsh5 || (record.msh5 || '').toLowerCase().includes(filterMsh5.toLowerCase());
                        const matchesMonitored = !filterMonitored || (filterMonitored === 'true' ? record.is_monitored : !record.is_monitored);
                        return matchesManufacturer && matchesAccount && matchesFilePrefix && matchesServerId && matchesDataInterval && matchesMsh5 && matchesMonitored;
                      })
                    )
                      .map((record) => {
                        const rowKey = `${record.manufacturer_id}-${record.account_id}`;
                        
                        return (
                          <TableRow 
                            key={rowKey} 
                            sx={{ 
                              '&:hover': { backgroundColor: 'grey.50' },
                              cursor: canEdit ? 'pointer' : 'default'
                            }}
                            onClick={() => canEdit && openEditDialog(record)}
                          >
                            <TableCell sx={{ py: 0.50 }}>{record.manufacturer_name}</TableCell>
                            <TableCell sx={{ py: 0.50 }}>{record.account_name}</TableCell>
                            <TableCell sx={{ py: 0.50 }}>
                              <Box sx={{ wordBreak: 'break-word' }}>{record.file_path_prefix}</Box>
                            </TableCell>
                            <TableCell sx={{ py: 0.50 }}>
                              <Box sx={{ wordBreak: 'break-word' }}>{getServerName(record)}</Box>
                            </TableCell>
                            <TableCell sx={{ py: 0.50 }}>
                              <Box sx={{ wordBreak: 'break-word' }}>{record.data_interval ? `${record.data_interval} hr` : ''}</Box>
                            </TableCell>
                            <TableCell sx={{ py: 0.50 }}>
                              <Box sx={{ wordBreak: 'break-word' }}>{record.msh5 || ''}</Box>
                            </TableCell>
                            <TableCell sx={{ py: 0.50 }}>
                              <Checkbox
                                checked={record.is_monitored}
                                disabled
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
        </Table>
      </TableContainer>

      {/* Add Relationship Dialog */}
      <Dialog 
        open={addDialogOpen} 
        onClose={() => setAddDialogOpen(false)}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: { borderRadius: 3 }
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Add New Monitored Account
          </Typography>
          <IconButton
            onClick={() => {
              setAddDialogOpen(false);
              setForm({ manufacturer_name: '', account_name: '', file_path_prefix: '', server_id: '', data_interval: '', msh5: '', is_monitored: true });
              setErrorMessage('');
            }}
            size="small"
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <form onSubmit={addRecord}>
          <DialogContent sx={{ pt: 2 }}>
            {errorMessage && (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 1 }}>
                {errorMessage}
              </Box>
            )}
            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <TextField
                  select
                  fullWidth
                  label="Manufacturer"
                  value={form.manufacturer_name}
                  onChange={(e) => setForm({...form, manufacturer_name: e.target.value})}
                  required
                  size="small"
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                >
                  {manufacturers.map(m => (
                    <MenuItem key={m.manufacturer_id} value={m.manufacturer_name}>
                      {m.manufacturer_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Box sx={{ position: 'relative' }}>
                  <TextField
                    fullWidth
                    label="Account"
                    placeholder="Type to search account name..."
                    value={form.account_name}
                    onChange={(e) => {
                      setForm({...form, account_name: e.target.value});
                      setShowAccountSuggestions(true);
                    }}
                    onFocus={() => setShowAccountSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowAccountSuggestions(false), 200)}
                    required
                    size="small"
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                  />
                  {showAccountSuggestions && form.account_name && (
                    <Box sx={{ 
                      position: 'absolute', 
                      top: '100%', 
                      left: 0, 
                      right: 0, 
                      zIndex: 1000, 
                      bgcolor: 'white', 
                      border: '1px solid #ccc', 
                      borderRadius: 1, 
                      maxHeight: 200, 
                      overflow: 'auto',
                      boxShadow: 2
                    }}>
                      {Array.isArray(accounts) && accounts
                        .filter(a => a.account_name.toLowerCase().includes(form.account_name.toLowerCase()))
                        .slice(0, 10)
                        .map(a => (
                          <Box
                            key={a.account_id}
                            sx={{ p: 1, cursor: 'pointer', '&:hover': { bgcolor: 'grey.100' } }}
                            onClick={() => {
                              setForm({...form, account_name: a.account_name});
                              setShowAccountSuggestions(false);
                            }}
                          >
                            {a.account_name}
                          </Box>
                        ))}
                    </Box>
                  )}
                </Box>
              </Grid>
              
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <TextField
                  fullWidth
                  label="File Path Prefix"
                  value={form.file_path_prefix}
                  onChange={(e) => setForm({...form, file_path_prefix: e.target.value})}
                  required
                  size="small"
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <TextField
                  select
                  fullWidth
                  label="Server"
                  value={form.server_id}
                  onChange={(e) => setForm({...form, server_id: e.target.value})}
                  size="small"
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                >
                  <MenuItem value="">
                    <em>No Server</em>
                  </MenuItem>
                  {servers.map(s => (
                    <MenuItem key={s.server_id} value={s.server_id}>
                      {s.server_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <TextField
                  fullWidth
                  label="Data Interval"
                  type="number"
                  value={form.data_interval}
                  onChange={(e) => setForm({...form, data_interval: e.target.value})}
                  size="small"
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                  slotProps={{
                    htmlInput: { min: 1 }
                  }}
                />
              </Grid>
              
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <TextField
                  fullWidth
                  label="MSH-5"
                  value={form.msh5}
                  onChange={(e) => setForm({...form, msh5: e.target.value})}
                  size="small"
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.is_monitored}
                      onChange={(e) => setForm({...form, is_monitored: e.target.checked})}
                    />
                  }
                  label="Is Monitored"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2 }}>
            <Button 
              onClick={() => {
                setAddDialogOpen(false);
                setForm({ manufacturer_name: '', account_name: '', file_path_prefix: '', server_id: '', data_interval: '', msh5: '', is_monitored: true });
                setErrorMessage('');
              }}
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained"
              sx={{ 
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                px: 3
              }}
            >
              Add New
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Edit Monitored Account Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: { borderRadius: 3 }
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Edit Monitored Account
          </Typography>
          <IconButton
            onClick={() => {
              setEditDialogOpen(false);
              setSelectedRecord(null);
              setErrorMessage('');
            }}
            size="small"
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleEditSubmit}>
          <DialogContent sx={{ pt: 2 }}>
            {errorMessage && (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 1 }}>
                {errorMessage}
              </Box>
            )}
            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <TextField
                  select
                  fullWidth
                  label="Manufacturer"
                  value={editForm.manufacturer_name}
                  onChange={(e) => setEditForm({...editForm, manufacturer_name: e.target.value})}
                  required
                  size="small"
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                >
                  {manufacturers.map(m => (
                    <MenuItem key={m.manufacturer_id} value={m.manufacturer_name}>
                      {m.manufacturer_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Box sx={{ position: 'relative' }}>
                  <TextField
                    fullWidth
                    label="Account"
                    placeholder="Type to search account name..."
                    value={editForm.account_name}
                    onChange={(e) => {
                      setEditForm({...editForm, account_name: e.target.value});
                      setShowEditAccountSuggestions(true);
                    }}
                    onFocus={() => setShowEditAccountSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowEditAccountSuggestions(false), 200)}
                    required
                    size="small"
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                  />
                  {showEditAccountSuggestions && editForm.account_name && (
                    <Box sx={{ 
                      position: 'absolute', 
                      top: '100%', 
                      left: 0, 
                      right: 0, 
                      zIndex: 1000, 
                      bgcolor: 'white', 
                      border: '1px solid #ccc', 
                      borderRadius: 1, 
                      maxHeight: 200, 
                      overflow: 'auto',
                      boxShadow: 2
                    }}>
                      {Array.isArray(accounts) && accounts
                        .filter(a => a.account_name.toLowerCase().includes(editForm.account_name.toLowerCase()))
                        .slice(0, 10)
                        .map(a => (
                          <Box
                            key={a.account_id}
                            sx={{ p: 1, cursor: 'pointer', '&:hover': { bgcolor: 'grey.100' } }}
                            onClick={() => {
                              setEditForm({...editForm, account_name: a.account_name});
                              setShowEditAccountSuggestions(false);
                            }}
                          >
                            {a.account_name}
                          </Box>
                        ))}
                    </Box>
                  )}
                </Box>
              </Grid>
              
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <TextField
                  fullWidth
                  label="File Path Prefix"
                  value={editForm.file_path_prefix}
                  onChange={(e) => setEditForm({...editForm, file_path_prefix: e.target.value})}
                  required
                  size="small"
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <TextField
                  select
                  fullWidth
                  label="Server"
                  value={editForm.server_id}
                  onChange={(e) => setEditForm({...editForm, server_id: e.target.value})}
                  size="small"
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                >
                  <MenuItem value="">
                    <em>No Server</em>
                  </MenuItem>
                  {servers.map(s => (
                    <MenuItem key={s.server_id} value={s.server_id}>
                      {s.server_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <TextField
                  fullWidth
                  label="Data Interval"
                  type="number"
                  value={editForm.data_interval}
                  onChange={(e) => setEditForm({...editForm, data_interval: e.target.value})}
                  size="small"
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                  slotProps={{
                    htmlInput: { min: 1 }
                  }}
                />
              </Grid>
              
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <TextField
                  fullWidth
                  label="MSH-5"
                  value={editForm.msh5}
                  onChange={(e) => setEditForm({...editForm, msh5: e.target.value})}
                  size="small"
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editForm.is_monitored}
                      onChange={(e) => setEditForm({...editForm, is_monitored: e.target.checked})}
                    />
                  }
                  label="Is Monitored"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2, justifyContent: 'space-between' }}>
            <Button 
              onClick={handleEditDelete}
              color="error"
              variant="outlined"
              sx={{ textTransform: 'none' }}
            >
              Delete
            </Button>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                onClick={() => {
                  setEditDialogOpen(false);
                  setSelectedRecord(null);
                  setErrorMessage('');
                }}
                sx={{ textTransform: 'none' }}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                variant="contained"
                sx={{ 
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 3
                }}
              >
                Save Changes
              </Button>
            </Box>
          </DialogActions>
        </form>
      </Dialog>
        </>
      )}
    </Box>
  );
}

export default MonitoredAccounts;